import {ApplicantType} from 'ApplicationEntry/models/ApplicantType';
import {Sex} from 'ApplicationEntry/models/Sex';
import {
  isValidSocialSecurityNumber,
  SocialSecurityNumber,
  ValidSocialSecurityNumber,
  EmptySocialSecurityNumber,
} from 'ApplicationEntry/models/SocialSecurityNumber';
import {isValidZipCode, ValidZipCode, ZipCode, EmptyZipCode} from 'ApplicationEntry/models/ZipCode';
import {EmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {PhoneNumber, isValidPhoneNumber, ValidPhoneNumber, EmptyPhoneNumber} from 'ApplicationEntry/models/PhoneNumber';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {isValidDate} from 'ApplicationEntry/models/Date';
import {OccupationError, UserAssignedOccupation, Occupation} from './Occupation';
import {Async} from 'Core/models/Async';

export class InvalidNewYorkApplicantDemographics {
  public readonly key = 'InvalidNewYorkApplicantDemographics';

  constructor(
    public readonly name: StringUnion,
    public readonly type: ApplicantType | '',
    public readonly sex: Sex | '',
    public readonly dateOfBirth: Date | '',
    public readonly address: StringUnion,
    public readonly addressLine2: StringUnion,
    public readonly city: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ZipCode,
    public readonly socialSecurityNumber: SocialSecurityNumber,
    public readonly phone: PhoneNumber,
    public readonly occupation: Occupation
  ) {}
}

type CreateFromApp = {
  name: StringUnion;
  type: ApplicantType | '';
  sex: Sex | '';
  dateOfBirth: Date | '';
  address: StringUnion;
  addressLine2: StringUnion;
  city: StringUnion;
  state: StringUnion;
  zip: ZipCode;
  socialSecurityNumber: SocialSecurityNumber;
  phone: PhoneNumber;
  occupation: Occupation;
};

export class ValidNewYorkApplicantDemographics {
  public static createFromApp({
    name,
    type,
    sex,
    dateOfBirth,
    address,
    addressLine2,
    city,
    state,
    zip,
    socialSecurityNumber,
    phone,
    occupation,
  }: CreateFromApp) {
    return this.create(
      name,
      type,
      sex,
      dateOfBirth,
      address,
      addressLine2,
      city,
      state,
      zip,
      socialSecurityNumber,
      phone,
      occupation
    );
  }

  public static create(
    name: StringUnion,
    type: ApplicantType | '',
    sex: Sex | '',
    dateOfBirth: Date | '',
    address: StringUnion,
    addressLine2: StringUnion,
    city: StringUnion,
    state: StringUnion,
    zip: ZipCode,
    socialSecurityNumber: SocialSecurityNumber,
    phone: PhoneNumber,
    occupation: Occupation
  ) {
    return isValidSocialSecurityNumber(socialSecurityNumber) &&
      isValidZipCode(zip) &&
      isValidPhoneNumber(phone) &&
      isValidDate(dateOfBirth)
      ? new ValidNewYorkApplicantDemographics(
          name,
          type,
          sex,
          dateOfBirth,
          address,
          addressLine2,
          city,
          state,
          zip,
          socialSecurityNumber,
          phone,
          occupation
        )
      : new InvalidNewYorkApplicantDemographics(
          name,
          type,
          sex,
          dateOfBirth,
          address,
          addressLine2,
          city,
          state,
          zip,
          socialSecurityNumber,
          phone,
          occupation
        );
  }

  public readonly key = 'ValidNewYorkApplicantDemographics';

  constructor(
    public readonly name: StringUnion,
    public readonly type: ApplicantType | '',
    public readonly sex: Sex | '',
    public readonly dateOfBirth: Date | '',
    public readonly address: StringUnion,
    public readonly addressLine2: StringUnion,
    public readonly city: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ValidZipCode,
    public readonly socialSecurityNumber: ValidSocialSecurityNumber,
    public readonly phone: ValidPhoneNumber,
    public readonly occupation: Occupation
  ) {}
}

export type NewYorkApplicantDemographics = ValidNewYorkApplicantDemographics | InvalidNewYorkApplicantDemographics;

export type NewYorkApplicantDemographicsOptions<TReturn> = ToMatchOptions<NewYorkApplicantDemographics, 'key', TReturn>;

export const matchNewYorkApplicantDemographics = <TReturn>(
  applicantDemographics: NewYorkApplicantDemographics,
  options: NewYorkApplicantDemographicsOptions<TReturn>
): TReturn => {
  switch (applicantDemographics.key) {
    case 'InvalidNewYorkApplicantDemographics':
      return options.InvalidNewYorkApplicantDemographics(applicantDemographics);
    case 'ValidNewYorkApplicantDemographics':
      return options.ValidNewYorkApplicantDemographics(applicantDemographics);
  }
};

export const changeName = (name: string) => (app: NewYorkApplicantDemographics) =>
  ValidNewYorkApplicantDemographics.createFromApp({
    ...app,
    name: NonEmptyString.create(name),
  });

export const changeType = (type: ApplicantType) => (app: NewYorkApplicantDemographics) =>
  ValidNewYorkApplicantDemographics.createFromApp({...app, type});

export const changeSex = (sex: Sex) => (app: NewYorkApplicantDemographics) =>
  ValidNewYorkApplicantDemographics.createFromApp({...app, sex});

export const changeDateOfBirth = (dateOfBirth: Date | '') => (app: NewYorkApplicantDemographics) =>
  ValidNewYorkApplicantDemographics.createFromApp({...app, dateOfBirth});

export const changeAddress = (address: string) => (app: NewYorkApplicantDemographics) =>
  ValidNewYorkApplicantDemographics.createFromApp({...app, address: NonEmptyString.create(address)});

export const changeAddressLine2 = (addressLine2: string) => (app: NewYorkApplicantDemographics) =>
  ValidNewYorkApplicantDemographics.createFromApp({...app, addressLine2: NonEmptyString.create(addressLine2)});

export const changeCity = (city: string) => (app: NewYorkApplicantDemographics) =>
  ValidNewYorkApplicantDemographics.createFromApp({...app, city: NonEmptyString.create(city)});

export const changeState = (state: string) => (app: NewYorkApplicantDemographics) =>
  ValidNewYorkApplicantDemographics.createFromApp({...app, state: NonEmptyString.create(state)});

export const changeZip = (zip: ZipCode) => (app: NewYorkApplicantDemographics) =>
  ValidNewYorkApplicantDemographics.createFromApp({...app, zip});

export const changeSocialSecurityNumber = (socialSecurityNumber: string) => (app: NewYorkApplicantDemographics) =>
  ValidNewYorkApplicantDemographics.createFromApp({
    ...app,
    socialSecurityNumber: ValidSocialSecurityNumber.create(socialSecurityNumber),
  });

export const changePhone = (phone: string) => (app: NewYorkApplicantDemographics) =>
  ValidNewYorkApplicantDemographics.createFromApp({
    ...app,
    phone: ValidPhoneNumber.create(phone),
  });

export const changeOccupation = (occupation: Async<string>) => (app: NewYorkApplicantDemographics) =>
  ValidNewYorkApplicantDemographics.createFromApp({...app, occupation});

export const changeUserOccupation = (
  occupation: string,
  code: string,
  description: string,
  underwritingCode: string
) => (app: NewYorkApplicantDemographics) =>
  ValidNewYorkApplicantDemographics.createFromApp({
    ...app,
    occupation: UserAssignedOccupation.create(occupation, code, description, underwritingCode),
  });

export const changeOccupationError = (occupation: string) => (app: NewYorkApplicantDemographics) =>
  ValidNewYorkApplicantDemographics.createFromApp({
    ...app,
    occupation: OccupationError.create(occupation),
  });

export const EmptyNewYorkApplicantDemographics = new ValidNewYorkApplicantDemographics(
  new EmptyString(),
  '',
  '',
  '',
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  EmptyZipCode,
  EmptySocialSecurityNumber,
  EmptyPhoneNumber,
  new UserAssignedOccupation('', '', '', '')
);
